import { useContext, useEffect, useMemo, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import businessApi from '../../../../../../../api/business.api'
import GlobalContext from '../../../../../../../context/global-context'
import {
  currentMonth,
  currentYear,
  monthsOptions,
  yearsOptions
} from '../../../../../../utils/constant'
import { formatCurrency, formatDate } from '../../../../../../utils/formatter'
import FilterControl from '../../../../../table/FilterControl'
import CustomButton from '../../../../../ui/CustomButton'
import ResizableModal from '../../../../../ui/ResizableModal'
import { SearchInput, Title } from '../../../../../ui'

const RELATED_COMPANY_FOLIOS = new Set([33, 34])
const FOLIO_LABELS = {
  33: 'Aporte Empresa Relacionada',
  34: 'Devolución Empresa Relacionada'
}

const normalize = value =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const toRows = payload => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.content)) return payload.content
  if (Array.isArray(payload?.libros)) return payload.libros
  if (payload && typeof payload === 'object') {
    return Object.values(payload)
      .flat()
      .filter(item => item && typeof item === 'object')
  }
  return []
}

const getAccountLabel = row =>
  [row?.codigoCuenta, row?.nombreCuenta].filter(Boolean).join(' - ') || '-'

const getAccountValue = row =>
  [row?.codigoCuenta, row?.nombreCuenta].filter(Boolean).join('|')

const getAmount = row =>
  Number(
    row?.montoTotal ??
      row?.monto ??
      row?.montoRelacionado ??
      row?.debe ??
      row?.haber ??
      row?.montoCargo ??
      row?.montoAbono ??
      0
  )

const getStatusBadgeClass = status => {
  const normalized = normalize(status)
  const isPartial = normalized.includes('parcial')
  const isPending = normalized.includes('pendiente') ||
    normalized.includes('por pagar') ||
    normalized.includes('por cobrar')
  const isCompleted = ['conciliado', 'pagado', 'cobrado']
    .some(value => normalized.includes(value))

  return [
    'inline-flex rounded-full px-2 py-1 text-[11px] font-semibold',
    isPartial
      ? 'bg-amber-100 text-amber-700'
      : isPending
        ? 'bg-danger/10 text-danger'
        : isCompleted
          ? 'bg-success/10 text-success'
          : 'bg-border/40 text-muted'
  ].join(' ')
}

const CuentaCorrienteEmpresaRelacionada = ({ open, setOpen }) => {
  const { currentBusiness } = useContext(GlobalContext)
  const [monthFilter, setMonthFilter] = useState(currentMonth)
  const [yearFilter, setYearFilter] = useState(currentYear)
  const [accountFilter, setAccountFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const monthsWithAll = useMemo(
    () => [
      { value: 0, label: 'Todos los meses' },
      ...monthsOptions.map(option => ({
        ...option,
        disabled: yearFilter === currentYear && option.value > currentMonth
      }))
    ],
    [yearFilter]
  )

  useEffect(() => {
    if (!open || !currentBusiness?.id) return

    let active = true
    const loadRows = async () => {
      setLoading(true)
      setLoadError(false)
      try {
        const response = await businessApi.getListSummaryVinculos({
          businessId: currentBusiness.id,
          month: Number(monthFilter),
          year: Number(yearFilter),
          linkage: undefined,
          limit: 10000
        })
        if (!active) return
        setRows(
          toRows(response?.data).filter(row =>
            RELATED_COMPANY_FOLIOS.has(Number(row?.folioCredito))
          )
        )
      } catch (error) {
        console.error('Error al cargar la cuenta corriente de empresa relacionada', error)
        if (active) {
          setRows([])
          setLoadError(true)
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    loadRows()
    return () => {
      active = false
    }
  }, [currentBusiness?.id, monthFilter, open, yearFilter])

  useEffect(() => {
    setAccountFilter('')
    setStatusFilter('')
  }, [currentBusiness?.id])

  const accountOptions = useMemo(() => {
    const options = new Map()
    rows.forEach(row => {
      const value = getAccountValue(row)
      if (value && !options.has(value)) {
        options.set(value, { value, label: getAccountLabel(row) })
      }
    })
    return [
      { value: '', label: 'Todas las cuentas' },
      ...Array.from(options.values()).sort((a, b) =>
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
      )
    ]
  }, [rows])

  const statusOptions = useMemo(() => {
    const statuses = new Set(
      rows.map(row => String(row?.status ?? '').trim()).filter(Boolean)
    )
    return [
      { value: '', label: 'Todos los estados' },
      ...Array.from(statuses)
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
        .map(status => ({ value: status, label: status }))
    ]
  }, [rows])

  const filteredRows = useMemo(() => {
    const query = normalize(searchQuery)
    return rows.filter(row => {
      if (accountFilter && getAccountValue(row) !== accountFilter) return false
      if (statusFilter && String(row?.status ?? '').trim() !== statusFilter) return false
      if (!query) return true

      return normalize(
        [
          row?.comentario,
          row?.codigoCuenta,
          row?.nombreCuenta,
          row?.rut,
          row?.razonSocial,
          FOLIO_LABELS[Number(row?.folioCredito)]
        ]
          .filter(Boolean)
          .join(' ')
      ).includes(query)
    })
  }, [accountFilter, rows, searchQuery, statusFilter])

  const accountBalances = useMemo(() => {
    const balances = new Map()

    filteredRows.forEach(row => {
      const accountKey = getAccountValue(row) || 'sin-cuenta'
      const amount = Math.abs(getAmount(row))
      const current = balances.get(accountKey) || {
        key: accountKey,
        label: getAccountLabel(row),
        charges: 0,
        payments: 0
      }

      if (Number(row?.folioCredito) === 33) {
        current.charges += amount
      } else if (Number(row?.folioCredito) === 34) {
        current.payments += amount
      }
      balances.set(accountKey, current)
    })

    return Array.from(balances.values())
      .map(account => ({
        ...account,
        balance: Math.abs(account.charges - account.payments)
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
      )
  }, [filteredRows])

  const generalBalance = useMemo(
    () => accountBalances.reduce((total, account) => total + account.balance, 0),
    [accountBalances]
  )

  return (
    <ResizableModal
      open={open}
      onClose={() => setOpen(false)}
      height={800}
      footer={
        <CustomButton
          title='CERRAR'
          color='primary'
          className='rounded-full'
          onClick={() => setOpen(false)}
        />
      }
    >
      <div className='flex h-full min-h-0 flex-col px-4 pt-4 font-sans'>
        <Title className='shrink-0 text-3xl'>Cuenta Corriente Empresa Relacionada</Title>

        <div className='mb-4 mt-8 grid shrink-0 grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-5'>
          <FilterControl
            label='Mes'
            value={monthFilter}
            onChange={value => setMonthFilter(Number(value))}
            options={monthsWithAll}
            fullWidth
          />
          <FilterControl
            label='Año'
            value={yearFilter}
            onChange={value => setYearFilter(Number(value))}
            options={yearsOptions}
            fullWidth
          />
          <FilterControl
            label='Cuenta'
            value={accountFilter}
            onChange={setAccountFilter}
            options={accountOptions}
            fullWidth
          />
          <FilterControl
            label='Estado'
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            fullWidth
          />
          <SearchInput
            placeholder='Buscar por descripción'
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            classNameBorder='rounded-full'
          />
        </div>

        <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-sm'>
          {loading ? (
            <div className='flex flex-1 items-center justify-center text-sm text-muted'>
              Cargando movimientos...
            </div>
          ) : loadError ? (
            <div className='flex flex-1 items-center justify-center text-sm text-danger'>
              No se pudieron cargar los movimientos. Intenta nuevamente.
            </div>
          ) : filteredRows.length === 0 ? (
            <div className='flex flex-1 flex-col items-center justify-center text-sm text-muted'>
              <SearchIcon className='mb-2' />
              No hay anticipos ni devoluciones para los filtros seleccionados.
            </div>
          ) : (
            <div className='flex h-full min-h-0 flex-col'>
              <div className='min-h-0 flex-1 overflow-auto'>
                <table className='table-report-auto min-w-[980px]'>
                <thead className='sticky top-0 z-10'>
                  <tr>
                    <th className='table-report-th w-[110px] text-left'>Fecha</th>
                    <th className='table-report-th w-[190px] text-left'>Tipo</th>
                    <th className='table-report-th w-[250px] text-left'>Cuenta</th>
                    <th className='table-report-th w-[280px] text-left'>Descripción</th>
                    <th className='table-report-th w-[120px] text-right'>Abono</th>
                    <th className='table-report-th w-[120px] text-right'>Cargo</th>
                    <th className='table-report-th w-[170px] text-left'>Status Conciliación</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, index) => {
                    const folioCredito = Number(row?.folioCredito)
                    const amount = Math.abs(getAmount(row))
                    const rowKey = row?.idPrincipal ?? row?.id ?? `${folioCredito}-${index}`
                    return (
                      <tr
                        key={rowKey}
                        className='table-report-row odd:bg-surface even:bg-border/20'
                      >
                        <td className='table-report-td whitespace-nowrap border-b border-border'>
                          {row?.fecha ? formatDate(row.fecha) : '-'}
                        </td>
                        <td className='table-report-td border-b border-border'>
                          {FOLIO_LABELS[folioCredito] || '-'}
                        </td>
                        <td className='table-report-td border-b border-border'>
                          <div className='max-w-[250px] whitespace-normal break-words text-[12px] leading-tight'>
                            {getAccountLabel(row)}
                          </div>
                        </td>
                        <td className='table-report-td border-b border-border'>
                          <div className='max-w-[280px] whitespace-normal break-words text-[12px] leading-tight'>
                            {row?.comentario || '-'}
                          </div>
                        </td>
                        <td className='table-report-td whitespace-nowrap border-b border-border text-right'>
                          {formatCurrency(folioCredito === 34 ? amount : 0)}
                        </td>
                        <td className='table-report-td whitespace-nowrap border-b border-border text-right'>
                          {formatCurrency(folioCredito === 33 ? amount : 0)}
                        </td>
                        <td className='table-report-td border-b border-border'>
                          <span
                            className={getStatusBadgeClass(row?.status)}
                          >
                            {row?.status || '-'}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                </table>
              </div>

              <div className='shrink-0 border-t border-border bg-gray-50 px-4 py-3'>
                <div className='flex flex-col items-end gap-2 text-[12px]'>
                  {accountBalances.map(account => (
                    <div
                      key={account.key}
                      className='flex w-full max-w-[520px] justify-between gap-6'
                    >
                      <span className='font-medium text-gray-600'>Saldo {account.label}</span>
                      <span className='font-semibold text-primary'>
                        {formatCurrency(account.balance)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className='mt-3 flex justify-end border-t border-gray-200 pt-2 text-sm font-bold'>
                  <div className='flex w-full max-w-[520px] justify-between gap-6'>
                    <span>Saldo general</span>
                    <span className='text-primary'>{formatCurrency(generalBalance)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ResizableModal>
  )
}

export default CuentaCorrienteEmpresaRelacionada
